import React, { useEffect, useState } from "react";
import { Send, Download, Copy, Loader2, RefreshCw, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePoll } from "@/hooks/usePoll";

const SendBox = () => {
  const { projectId, project } = useApp();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const make = async () => {
    setBusy(true);
    try { setCode((await api.transferCode(projectId)).code); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const copy = () => { navigator.clipboard?.writeText(code); toast.success("Transfer code copied"); };
  return (
    <div className="space-y-2" data-testid="transfer-send">
      <div className="text-sm font-semibold flex items-center gap-2"><Send size={14} className="text-[#E6B04E]" />Send "{project?.name || "no project selected"}"</div>
      <p className="text-[#64748B] text-xs">Make a code here, then paste it into the desktop app (Settings → Project Transfer → Receive). Keep this tab open while the previews copy across.</p>
      <div className="flex gap-2">
        <button className="btn btn-gold !py-1.5 text-sm shrink-0" onClick={make} disabled={busy || !projectId} data-testid="btn-transfer-make-code">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ArrowRightLeft size={14} />}Make transfer code</button>
        {code && <input readOnly value={code} className="studio-input font-mono text-[10px] flex-1" data-testid="transfer-code-output" onFocus={(e) => e.target.select()} />}
        {code && <button className="btn btn-ghost !py-1.5 text-sm" onClick={copy} data-testid="btn-transfer-copy-code"><Copy size={14} /></button>}
      </div>
    </div>
  );
};

const Incoming = ({ t, onRetry }) => {
  const objPct = t.objects_total ? Math.round((t.objects_done || 0) / t.objects_total * 100) : 100;
  return (
    <div className="border border-[#252A3B] rounded p-3 text-sm space-y-1" data-testid={`transfer-incoming-${t.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold">{t.project_name}</span>
        <span className="metric-label" data-testid={`transfer-status-${t.id}`}>{t.status.replace("_", " ")}</span>
      </div>
      <div className="metric-label">Previews & renders {t.objects_done || 0}/{t.objects_total} ({objPct}%) · originals from JioCloud {t.raw_done}/{t.raw_total}
        {t.raw_failed > 0 && <span className="text-[#fbbf24]"> · {t.raw_failed} failed</span>}</div>
      <div className="h-1 bg-[#252A3B] rounded"><div className="h-1 bg-[#E6B04E] rounded" style={{ width: `${t.raw_total ? Math.round(t.raw_done / t.raw_total * 100) : objPct}%` }} /></div>
      {t.raw_failed > 0 && <button className="btn btn-ghost !py-1 text-xs" onClick={() => onRetry(t.id)} data-testid={`btn-transfer-retry-${t.id}`}><RefreshCw size={12} />Retry failed</button>}
    </div>
  );
};

export const ReceiveBox = ({ onProject }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const { data, refresh } = usePoll(() => api.transfersIncoming(), 8000, []);
  const ready = (data || []).find((t) => t.objects_done > 0 || t.status !== "running");
  useEffect(() => { if (ready && onProject) onProject(ready.project_id); }, [ready?.project_id, onProject]); // eslint-disable-line
  const start = async () => {
    setBusy(true);
    try { await api.transferImport(code); toast.success("Transfer started"); setCode(""); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const retry = async (tid) => { const r = await api.transferRetry(tid); toast.success(`${r.requeued} job(s) retried`); refresh(); };
  return (
    <div className="space-y-2" data-testid="transfer-receive">
      <div className="text-sm font-semibold flex items-center gap-2"><Download size={14} className="text-[#10B981]" />Receive a project</div>
      <div className="flex gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Paste transfer code" className="studio-input font-mono text-xs flex-1" data-testid="transfer-code-input" />
        <button className="btn btn-gold !py-1.5 text-sm" onClick={start} disabled={busy || !code.trim()} data-testid="btn-transfer-start">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}Start transfer</button>
      </div>
      {(data || []).map((t) => <Incoming key={t.id} t={t} onRetry={retry} />)}
    </div>
  );
};

export const ProjectTransfer = () => (
  <div className="studio-card p-5 space-y-5" data-testid="project-transfer">
    <div className="flex items-center gap-2"><ArrowRightLeft size={16} className="text-[#E6B04E]" /><span className="font-head font-bold">Project Transfer</span></div>
    <p className="text-[#94A3B8] text-xs max-w-3xl">Moves a whole project (analysis, AI tags, story, edit plan, wizard progress, previews and renders) so nothing has to be re-analysed. Original camera files are downloaded again from their JioCloud links, starting with the clips in your edit.</p>
    <div className="grid lg:grid-cols-2 gap-6"><SendBox /><ReceiveBox /></div>
  </div>
);
